import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import Loading from '@components/core/loading';
import PageTitle from '@components/core/page-title/page-title';
import TwoColsSplitter from '@components/core/two-cols-splitter/index';
import { OpraReportTemplate, ReportModules } from '@interfaces/report-templates.interface';
import GeneratedReportsGrid from 'views/components/shared/manage-reports-templates/generated-reports-grid';
import ReportTemplatesGrid from 'views/components/shared/manage-reports-templates/reports-templates-grid';

import { useAuthorization, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import {
    addPrefixedOrganisationTemplateId,
    addPrefixedRecordIdsAndTemplateNamesToReportTemplates,
} from '@utils/prefixes-helper';
import { AuditServiceType } from '@types/graphql-global-types';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { useGetReportTemplatesForOrgByArea } from '@apis/bff-api/report-templates';
import './styles.scss';

const ManageReportsTemplates = ({ studyId, module }: { studyId: string; module: ReportModules }) => {
    const { t } = useTranslation();
    const user = useAuthorization();
    const orgKey = user?.organisationKey ?? Constants.ObjectId.Empty;

    const [currentTemplate, setCurrentTemplate] = useState<OpraReportTemplate>();

    const { data, isLoading, isValidating } = useGetReportTemplatesForOrgByArea(orgKey, module, studyId);

    const { data: prefixesData } = useQuery<auditRecordIdPrefixes>(AUDIT_RECORD_ID_PREFIXES, {
        fetchPolicy: 'cache-first',
    });

    const reportTemplates = useMemo(() => {
        if (!data || !prefixesData) return [];
        const templates = addPrefixedRecordIdsAndTemplateNamesToReportTemplates(
            data,
            prefixesData?.auditRecordIdPrefixes,
        );
        const fullyFormedTemplates = addPrefixedOrganisationTemplateId(
            templates,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        return orderBy(fullyFormedTemplates, [{ field: 'fullName', dir: 'asc' }]);
    }, [data, prefixesData]);

    if (isLoading || isValidating) return <Loading />;

    return (
        <>
            <PageTitle text={t('manageReports')} className="mb-10" />
            <TwoColsSplitter
                className="report-template-splitter"
                orientation="horizontal"
                alignmentValues={[
                    {
                        min: '30%',
                        size: '50%',
                        keepMounted: true,
                    },
                    { min: '30%' },
                ]}
                leftCol={
                    <ReportTemplatesGrid
                        module={module}
                        currentTemplate={currentTemplate}
                        setCurrentTemplate={setCurrentTemplate}
                        gridData={reportTemplates}
                    />
                }
                rightCol={<GeneratedReportsGrid orgKey={orgKey} module={module} currentTemplate={currentTemplate} />}
            />
        </>
    );
};

export default ManageReportsTemplates;
